import React, { useState } from 'react';
import { Avatar } from '../../BasicComponents/Avatar';
import { Badge } from '../../BasicComponents/Badge';
import { Skeleton } from '../../BasicComponents/Skeleton';
import { STATUS_COLORS } from '../../../constants/status-colors';
import type { StatusKey } from '../../../constants/status-colors';
import type { TimelineActor, TimelineItem, TimelineProps } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const SKELETON_COUNT = 4;

/**
 * Dot colour for timeline items — pulled from STATUS_COLORS.dot.
 * Falls back to neutral when no type is provided.
 */
function dotColorClass(type?: string): string {
  if (type && type in STATUS_COLORS) {
    return STATUS_COLORS[type as StatusKey].dot;
  }
  return 'bg-neutral-400 dark:bg-neutral-500';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveActor(actor: TimelineActor | string | undefined): TimelineActor | null {
  if (!actor) return null;
  if (typeof actor === 'string') return { name: actor };
  return actor;
}

function formatTimestamp(ts: string | Date): { display: string; iso: string } {
  const date = typeof ts === 'string' ? new Date(ts) : ts;
  const iso = date.toISOString();

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let display: string;
  if (diffMins < 1) {
    display = 'just now';
  } else if (diffMins < 60) {
    display = `${diffMins}m ago`;
  } else if (diffHours < 24) {
    display = `${diffHours}h ago`;
  } else if (diffDays < 7) {
    display = `${diffDays}d ago`;
  } else {
    display = date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }

  return { display, iso };
}

// ─── Skeleton item ────────────────────────────────────────────────────────────

function TimelineSkeletonItem({ isLast }: { isLast: boolean }) {
  return (
    <li role="listitem" className="flex gap-3 relative" aria-hidden="true">
      {/* Dot + line */}
      <div className="flex flex-col items-center shrink-0">
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200 dark:bg-neutral-700 animate-pulse-soft mt-1" />
        {!isLast && (
          <span className="flex-1 w-px bg-neutral-200 dark:bg-neutral-700 mt-1" />
        )}
      </div>
      {/* Content */}
      <div className="flex-1 pb-6 space-y-2">
        <Skeleton variant="text" width="40%" height={12} />
        <Skeleton variant="text" width="70%" height={14} />
        <Skeleton variant="text" width="55%" height={12} />
      </div>
    </li>
  );
}

// ─── Default variant item ─────────────────────────────────────────────────────

function DefaultItem({
  item,
  isLast,
}: {
  item: TimelineItem;
  isLast: boolean;
}) {
  const actor = resolveActor(item.actor);
  const { display, iso } = formatTimestamp(item.timestamp);
  const dot = dotColorClass(item.type);

  return (
    <li role="listitem" className="flex gap-3 relative">
      {/* Left column: dot / icon + connector */}
      <div className="flex flex-col items-center shrink-0">
        {item.icon ? (
          <span className="flex items-center justify-center h-7 w-7 rounded-full bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 mt-0.5 shrink-0">
            <span className="h-3.5 w-3.5 flex items-center" aria-hidden="true">
              {item.icon}
            </span>
          </span>
        ) : (
          <span className={`h-2.5 w-2.5 rounded-full shrink-0 mt-1.5 ${dot}`} aria-hidden="true" />
        )}
        {!isLast && (
          <span className="flex-1 w-px bg-neutral-200 dark:border-neutral-700 bg-neutral-200 dark:bg-neutral-700 mt-1" />
        )}
      </div>

      {/* Right column: content */}
      <div className="flex-1 pb-6 min-w-0">
        {/* Top row: actor avatar + name + timestamp */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
          {actor && (
            <Avatar name={actor.name} src={actor.avatarSrc} size="sm" />
          )}
          {actor && (
            <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
              {actor.name}
            </span>
          )}
          {item.type && (
            <Badge variant={item.type as StatusKey} size="sm">
              {item.label ?? item.type}
            </Badge>
          )}
          <time
            dateTime={iso}
            className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 whitespace-nowrap"
            title={new Date(iso).toLocaleString('en-IN')}
          >
            {display}
          </time>
        </div>

        {/* Action */}
        <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">
          {item.action}
        </p>

        {/* Optional description */}
        {item.description && (
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
            {item.description}
          </p>
        )}
      </div>
    </li>
  );
}

// ─── Compact variant item ─────────────────────────────────────────────────────

function CompactItem({
  item,
  isLast,
}: {
  item: TimelineItem;
  isLast: boolean;
}) {
  const actor = resolveActor(item.actor);
  const { display, iso } = formatTimestamp(item.timestamp);
  const dot = dotColorClass(item.type);

  return (
    <li role="listitem" className="flex gap-2 relative">
      {/* Dot + connector */}
      <div className="flex flex-col items-center shrink-0">
        <span className={`h-2 w-2 rounded-full shrink-0 mt-1.5 ${dot}`} aria-hidden="true" />
        {!isLast && (
          <span className="flex-1 w-px bg-neutral-200 dark:bg-neutral-700 mt-1" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-4 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs text-neutral-700 dark:text-neutral-300 leading-snug truncate">
            {actor ? (
              <span className="font-medium text-neutral-800 dark:text-neutral-200">
                {actor.name}{' '}
              </span>
            ) : null}
            {item.action}
          </p>
          <time
            dateTime={iso}
            className="text-[10px] text-neutral-400 dark:text-neutral-500 whitespace-nowrap shrink-0"
            title={new Date(iso).toLocaleString('en-IN')}
          >
            {display}
          </time>
        </div>
        {item.description && (
          <p className="mt-0.5 text-[10px] text-neutral-400 dark:text-neutral-500 leading-relaxed line-clamp-2">
            {item.description}
          </p>
        )}
      </div>
    </li>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

export function Timeline({
  items,
  loading = false,
  maxItems,
  variant = 'default',
}: TimelineProps) {
  const [expanded, setExpanded] = useState(false);

  // Determine visible items
  const shouldTruncate = maxItems !== undefined && items.length > maxItems && !expanded;
  const visible = shouldTruncate ? items.slice(0, maxItems) : items;

  const isEmpty = !loading && items.length === 0;

  return (
    <div className="w-full">
      <ul role="list" className="space-y-0">
        {/* Loading skeletons */}
        {loading &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <TimelineSkeletonItem key={i} isLast={i === SKELETON_COUNT - 1} />
          ))}

        {/* Empty state */}
        {isEmpty && (
          <li role="listitem" className="py-8 text-center">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">No activity yet</p>
          </li>
        )}

        {/* Items */}
        {!loading &&
          visible.map((item, index) => {
            const isLast = index === visible.length - 1 && !shouldTruncate;
            return variant === 'compact' ? (
              <CompactItem key={item.id} item={item} isLast={isLast} />
            ) : (
              <DefaultItem key={item.id} item={item} isLast={isLast} />
            );
          })}
      </ul>

      {/* Show more / less */}
      {!loading && maxItems !== undefined && items.length > maxItems && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        >
          {expanded
            ? 'Show less'
            : `Show ${items.length - maxItems} more`}
        </button>
      )}
    </div>
  );
}
